/**
 * E2E renderer probe for controls_connect_succeeded.
 *
 * WHY: the success name has no desktop expectCanonicalEvent receipt. HIL saw it
 * fire on a real camera, then timed out waiting for media success. This probe
 * drives the same tracker the room observer calls, behind the same track().
 * Mirrors onboarding-telemetry-probe.ts.
 * Per rule: stay-in-scope + investigate-before-adding (ai.knowledge/global).
 */
import { EVENT_CATEGORIES } from '#~/analytics/constants/index.js';
import { createMediaControlsDiscoveryTracker } from '#~/analytics/domains/media-controls-discovery-lifecycle.js';
import type { AnalyticsTrackFn } from '#~/analytics/domains/types.js';
import type { EventCategory } from '#~/analytics/constants/event-categories.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { track as trackAnalyticsEvent } from '#~/analytics/track.js';

interface MediaControlsTelemetryProbe {
  controlsConnectSucceeded(properties: EventProperties): void;
}

const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

export function sendMediaControlsProbeEvent(
  name: string,
  category: EventCategory,
  properties?: EventProperties
): void {
  if (category !== EVENT_CATEGORIES.DEVICE) return;
  void trackAnalyticsEvent({
    name,
    category,
    ...(properties !== undefined ? { properties } : {}),
    timestamp: Date.now()
  });
}

const trackMediaControls: AnalyticsTrackFn = (name, category, properties) => {
  sendMediaControlsProbeEvent(name, category, properties);
};

if (isE2eEnabled()) {
  const tracker = createMediaControlsDiscoveryTracker(trackMediaControls);
  const probe: MediaControlsTelemetryProbe = {
    controlsConnectSucceeded(properties) {
      tracker.trackControlsConnectSuccess(properties);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisMediaControlsTelemetry = probe;
}
